import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer, ArrowLeft, Save } from "lucide-react";
import { ThermalLabel } from "@/components/ThermalLabel";

export default function LabelPrint() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [width, setWidth] = useState(Number(localStorage.getItem("labelWidth")) || 80);
  const [remarks, setRemarks] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ids = (params.get("ids") || "").split(",").filter(Boolean);
    const req = ids.length
      ? api.post("/jobs/labels", ids)
      : api.get("/jobs", { params: Object.fromEntries(["status", "q", "source", "agent_id"].filter((k) => params.get(k)).map((k) => [k, params.get(k)])) });
    req.then((r) => { setJobs(r.data); setRemarks(Object.fromEntries(r.data.map((j) => [j.id, j.label_remark || ""]))); });
  }, [params]);

  const setW = (w) => { setWidth(w); localStorage.setItem("labelWidth", String(w)); };

  const saveRemarks = async () => {
    setSaving(true);
    const changed = jobs.filter((j) => (remarks[j.id] || "") !== (j.label_remark || ""));
    await Promise.all(changed.map((j) => api.patch(`/jobs/${j.id}`, { label_remark: remarks[j.id] || "" })));
    setJobs(jobs.map((j) => ({ ...j, label_remark: remarks[j.id] || "" })));
    setSaving(false);
    if (changed.length) toast.success(`Saved ${changed.length} remark(s)`);
  };

  const print = async () => { await saveRemarks(); setTimeout(() => window.print(), 150); };

  if (!jobs) return <div className="p-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="min-h-screen bg-neutral-200 print:bg-white" data-testid="label-print-page">
      <style>{`
        @media print {
          @page { size: ${width}mm auto; margin: 0; }
          body { background: white; }
          .thermal-label { page-break-after: always; break-after: page; }
          .thermal-label:last-child { page-break-after: auto; }
        }
      `}</style>
      <div className="print:hidden sticky top-0 z-10 flex flex-wrap items-center gap-2 p-3 border-b bg-white">
        <Button variant="outline" size="sm" onClick={() => nav(-1)} data-testid="label-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="font-semibold text-sm">{jobs.length} label(s)</div>
        <div className="flex items-center gap-1 ml-2 text-xs">
          <span className="text-muted-foreground">Paper:</span>
          {[80, 58].map((w) => <Button key={w} size="sm" variant={width === w ? "default" : "outline"} className="h-7" onClick={() => setW(w)} data-testid={`label-width-${w}`}>{w}mm</Button>)}
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={saveRemarks} disabled={saving} data-testid="label-save-remarks"><Save className="w-4 h-4 mr-1" /> Save Remarks</Button>
          <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={print} data-testid="label-print"><Printer className="w-4 h-4 mr-1" /> Print {jobs.length > 1 ? "All" : ""}</Button>
        </div>
      </div>
      <div className="print:hidden text-[11px] text-muted-foreground px-4 pt-3">Tip: in the print dialog choose your thermal printer, paper width {width}mm, margins "None", and disable headers/footers. Remarks typed here are printed on the label and saved to the job.</div>
      <div className="p-4 print:p-0 flex flex-col items-center gap-4 print:gap-0">
        {jobs.map((j) => (
          <div key={j.id} className="shadow print:shadow-none">
            <ThermalLabel job={j} width={width} editable remark={remarks[j.id]} onRemark={(v) => setRemarks((r) => ({ ...r, [j.id]: v }))} />
          </div>
        ))}
        {jobs.length === 0 && <div className="text-muted-foreground p-10">No jobs match.</div>}
      </div>
    </div>
  );
}
